import type { ProductLine } from "@/types/common";
import { GA4option } from "@/types/ga4";
import { ProductGender, ProductGenderGA4 } from "@/types/magento";

/*
 * The input allows ProductGender ("Unisex") in addition to ProductGenderGA4 so that
 * callers can spread AtcOption/MagentoProduct directly. The function resolves the correct
 * GA4 value internally via product_gender_new ?? product_gender.
 */
type GA4ItemInput = Omit<Partial<GA4option>, "product_gender"> & {
  product_gender?: ProductGender | ProductGenderGA4;
};

export const getItemReportingAttributes = (product: GA4ItemInput) => {
  const variant = product?.shipping_interval ? "subscription" : "one-time";
  const gender = product?.product_gender_new ?? product?.product_gender;
  const isMen = gender === "Men";
  const isBrand = ["All", "Unisex"].includes(gender);
  const product_line = (isBrand ? "neutral" : isMen ? "men" : "women") as ProductLine;
  /*
   * Fix one time price bug. Ignore bundled_price locally instead of nulling it on the input:
   * callers pass the shared option object (e.g. onProductViewed), and mutating it strips the
   * promo price from every other consumer such as the sticky header.
   */
  const bundledPrice = product?.shipping_interval ? product?.bundled_price : null;
  const price =
    bundledPrice ||
    product?.price ||
    product?.new_price ||
    product?.ui_data?.price ||
    product?.value;
  // 1. Convert inputs to numbers (or undefined if they don't exist)
  const oldNum = product?.old_price ? Number(product?.old_price) : undefined;
  const currentNum = price ? Number(price) : undefined;

  // 2. Safely compare and calculate
  const discount = oldNum && currentNum && oldNum > currentNum ? oldNum - currentNum : 0;

  // Mapping to the GA4 Items Array
  const item = {
    id: product?.sku,
    sku: product?.sku,
    name: product?.name,
    variant,
    price,
    product_line,
    quantity: product?.quantity || product?.qty || 1,
    index: 1,
    product_index: 1,
    purchase_type: product?.purchase_type,
    product_category: product?.product_category,
    product_gender: product?.product_gender_new ?? product?.product_gender,
    core_category: product?.product_core_category,
    image_url: product?.image?.url || product?.label1,
    subscription_type: product?.subscription_type,
    billing_interval: product?.billing_interval,
    shipping_interval: product?.shipping_interval,
    discount: typeof discount === "number" ? +discount?.toFixed(2) : 0,
    quiz_reco: !!product?.quiz_reco,
    /*
     * List/Promotion Context (Optional)
     * promotion_id: product?.promotion_id,
     * promotion_name: product?.promotion_name,
     * creative_name: product?.creative_name,
     * creative_slot: product?.creative_slot,
     */
    type: null as string | null,
    list_id: null as string | null,
    list_type: null as string | null,
    list_name: null as string | null,
    purchase_source: null as string | null,
    source: null as string | null,
    coupon: product?.coupon || product?.ui_data?.code || null,
  };

  return item;
};
